import React from 'react';
import { observer } from 'mobx-react-lite';
import { CopyIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { Checkbox } from '@producer/ui-kit';

import { ListViewItem } from '../../../components/list-view/list-view';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { RenameDialog } from '../../../components/dialogs/rename.dialog';
import { openModal } from '../../../core/modal/open-modal';
import { useSelection } from '../../../core/contexts/selection.context';
import { Storyboard } from '../../../app/entities';

import styles from './storyboard-list-item.module.scss';

type StoryboardListItemProps = {
  storyboard: Storyboard;
  displayName: string;
  onOpen: (storyboardId: string) => void;
};

export const StoryboardListItem: React.FC<StoryboardListItemProps> = observer(
  ({ storyboard, displayName, onOpen }) => {
    const { elements, select, clear } = useSelection();

    const isSelected = elements.includes(storyboard._id);

    /**
     * A menu opened on a row inside a multi-selection acts on the whole selection — the same
     * convention as the shot list's row menu. A menu opened on a row outside the selection acts
     * on that row alone.
     */
    const selectedStoryboards =
      isSelected && elements.length > 1
        ? (elements.map((id) => Storyboard.getOne(id)).filter(Boolean) as Storyboard[])
        : [storyboard];

    const isBulk = selectedStoryboards.length > 1;

    /**
     * Selection lives entirely on the check box: clicking the row itself opens the board. A plain
     * click toggles this row alone ('ctrl' is the provider's toggle modifier), shift extends from
     * the current selection.
     */
    const handleSelect = (e: React.MouseEvent) => {
      e.stopPropagation();
      select(storyboard._id, e.shiftKey ? 'shift' : 'ctrl');
    };

    const handleDuplicate = async () => {
      /**
       * Sequential rather than parallel: each duplicate writes the project's `lastUpdatedAt` and
       * creates a step, and a failure part-way through leaves the boards already copied intact.
       */
      let lastCopy: Storyboard | undefined;

      for (const board of selectedStoryboards) {
        lastCopy = (await board.duplicate()) ?? lastCopy;
      }

      clear();

      if (!isBulk && lastCopy) {
        onOpen(lastCopy._id);
      }
    };

    const handleDelete = async () => {
      for (const board of selectedStoryboards) {
        await board.delete();
      }

      clear();
    };

    const items: React.ComponentProps<typeof DropdownMenu>['items'] = [
      ...(isBulk
        ? []
        : [
            {
              title: 'Rename storyboard',
              icon: <Pencil1Icon />,
              onSelect: () =>
                openModal(RenameDialog, {
                  defaultName: storyboard.name,
                  onSubmit: (name: string) => storyboard.update({ name }),
                  title: 'Rename storyboard',
                  label: 'File name',
                }),
            },
          ]),
      {
        title: isBulk
          ? `Duplicate ${selectedStoryboards.length} storyboards`
          : 'Duplicate storyboard',
        icon: <CopyIcon />,
        onSelect: handleDuplicate,
      },
      {
        title: isBulk ? `Delete ${selectedStoryboards.length} storyboards` : 'Delete storyboard',
        type: 'danger' as const,
        icon: <TrashIcon />,
        onSelect: () =>
          openModal(DeleteDialog, {
            onSubmit: handleDelete,
            title: isBulk ? 'Delete storyboards' : 'Delete storyboard',
            text: isBulk
              ? `Are you sure you want to delete these ${selectedStoryboards.length} storyboards?`
              : 'Are you sure you want to delete this storyboard?',
          }),
      },
    ];

    return (
      <ListViewItem
        className={styles['row']}
        onClick={() => onOpen(storyboard._id)}
        data-selected={isSelected}
      >
        {/**
         * The check box is hidden until the row is hovered or selected. `@media (hover: hover)`
         * scopes that to pointers that can actually hover — on touch there is no hover state to
         * reveal it with, so it stays visible.
         */}
        <td style={{ width: '4rem', paddingRight: '0.8rem' }} onClick={handleSelect}>
          <div className={styles['select-box']}>
            <Checkbox checked={isSelected} />
          </div>
        </td>

        <td style={{ width: '100%' }}>{displayName}</td>

        <td
          style={{ width: '4.8rem', paddingRight: '0.8rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles['cell-wrapper']}>
            <DropdownMenu items={items} />
          </div>
        </td>
      </ListViewItem>
    );
  },
);
